import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import BaseButton from "../../BaseComponents/BaseButton";
import * as yup from "yup";
import { useFormik } from "formik";
import { InputPlaceHolder, validationMessages } from "../../Constant/validation";
import { Submit, SuperAdmin, Update } from "../../Constant";
import BaseInput from "../../BaseComponents/BaseInput";
import { MenuConstants, checkStatusCodeSuccess, rendorBackButton } from "../../Constant/common";
import {
  addRoleApi,
  editRoleApi,
  listOfRolePermissionApi,
  viewRolePermissionApi,
} from "../../Api/roleApi";
import { listOfOrganizationApi } from "../../Api/OrganizationApi";
import { toast } from "react-toastify";
import BaseSelect from "../../BaseComponents/BaseSelect";
import {
  addRoleLabel,
  editRoleLabel,
  masterModulesLabel,
  roleNameLabel,
  viewRoleLabel,
} from "../../Constant/Role";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import {
  Shield,
  Check,
  ChevronDown,
  ChevronUp,
  Users,
  Grid,
  DollarSign,
  Zap,
  Settings,
  Lock,
  Bell,
  TrendingUp,
  Package,
  Globe,
  Smartphone,
  BarChart2,
  LifeBuoy,
} from "react-feather";
import { useSelectedOrganization } from "../OrganizationContext";

/* ── Module category icons (react-feather) ── */
const CATEGORY_ICON_MAP = {
  HR: Users,
  Master: Grid,
  Account: DollarSign,
  Mobilization: Zap,
  Setting: Settings,
  Role: Lock,
  Notification: Bell,
  Sales: TrendingUp,
  Other: Package,
  WEB: Globe,
  APP: Smartphone,
  SALES: BarChart2,
  SUPPORT: LifeBuoy,
};

/* ── PermissionToggle — individual module checkbox ── */
const PermissionToggle = ({ item, checked, onChange, disabled, title }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    aria-label={`${item.module_name} permission`}
    onClick={() => !disabled && onChange(item.id)}
    title={title}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "7px 12px",
      borderRadius: 10,
      cursor: disabled ? "not-allowed" : "pointer",
      border: `1.5px solid ${checked ? "rgba(99,102,241,0.4)" : "var(--glass-border)"}`,
      background: checked ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
      transition: "all 0.15s",
      userSelect: "none",
    }}
  >
    {/* Checkbox box */}
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        flexShrink: 0,
        background: checked ? "var(--brand-indigo)" : "transparent",
        border: `1.5px solid ${checked ? "var(--brand-indigo)" : "var(--glass-border)"}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: checked ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
        transition: "all 0.15s",
      }}
    >
      {checked && <Check size={11} strokeWidth={3} color="#fff" />}
    </div>
    <span
      style={{
        fontSize: "0.8125rem",
        fontWeight: checked ? 600 : 500,
        color: checked ? "var(--brand-indigo)" : "var(--text-secondary)",
        whiteSpace: "nowrap",
      }}
    >
      {item.module_name}
    </span>
  </button>
);

/* ── CategorySection — collapsible group of modules ── */
const CategorySection = ({
  categoryKey,
  modules,
  permissionAllowed,
  onToggle,
  onSelectAll,
  disabled,
  disabledReason,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const allSelected = modules.every((m) => permissionAllowed.includes(m.id));
  const someSelected = modules.some((m) => permissionAllowed.includes(m.id));
  const selectedCount = modules.filter((m) => permissionAllowed.includes(m.id)).length;
  const label = MenuConstants[categoryKey] || categoryKey;
  const CategoryIcon = CATEGORY_ICON_MAP[categoryKey] || Package;

  return (
    <div
      style={{
        borderRadius: 14,
        overflow: "hidden",
        border: `1px solid ${someSelected ? "rgba(99,102,241,0.2)" : "var(--glass-border)"}`,
        background: someSelected ? "rgba(99,102,241,0.02)" : "var(--glass-bg)",
        transition: "border-color 0.2s",
      }}
    >
      {/* Category header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          borderBottom: collapsed ? "none" : "1px solid var(--glass-border)",
          background: someSelected ? "rgba(99,102,241,0.04)" : "transparent",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Select-all checkbox */}
          <button
            type="button"
            role="checkbox"
            aria-checked={allSelected}
            aria-label={`Select all ${label} permissions`}
            onClick={() => !disabled && onSelectAll(categoryKey, !allSelected)}
            title={disabledReason}
            style={{
              width: 20,
              height: 20,
              borderRadius: 6,
              flexShrink: 0,
              background: allSelected
                ? "var(--brand-indigo)"
                : someSelected
                  ? "rgba(99,102,241,0.3)"
                  : "transparent",
              border: `1.5px solid ${allSelected || someSelected ? "var(--brand-indigo)" : "var(--glass-border)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: disabled ? "not-allowed" : "pointer",
              boxShadow: allSelected ? "0 2px 8px rgba(99,102,241,0.4)" : "none",
              transition: "all 0.15s",
            }}
          >
            {allSelected && <Check size={12} strokeWidth={3} color="#fff" />}
            {!allSelected && someSelected && (
              <div style={{ width: 8, height: 2, background: "#fff", borderRadius: 1 }} />
            )}
          </button>

          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              flexShrink: 0,
              background: someSelected ? "rgba(99,102,241,0.1)" : "var(--icon-btn-bg)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-hidden="true"
          >
            <CategoryIcon
              size={14}
              strokeWidth={2}
              style={{ color: someSelected ? "var(--brand-indigo)" : "var(--text-muted)" }}
            />
          </div>
          <span style={{ fontWeight: 700, fontSize: "0.9375rem", color: "var(--text-primary)" }}>
            {label}
          </span>
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: "0.6875rem",
              fontWeight: 700,
              background: someSelected ? "rgba(99,102,241,0.12)" : "var(--icon-btn-bg)",
              color: someSelected ? "var(--brand-indigo)" : "var(--text-muted)",
            }}
          >
            {selectedCount}/{modules.length}
          </span>
        </div>

        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand" : "Collapse"}
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            border: "1px solid var(--glass-border)",
            background: "var(--icon-btn-bg)",
            color: "var(--text-muted)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {collapsed ? (
            <ChevronDown size={14} strokeWidth={2} />
          ) : (
            <ChevronUp size={14} strokeWidth={2} />
          )}
        </button>
      </div>

      {/* Module toggles */}
      {!collapsed && (
        <div style={{ padding: "12px 16px", display: "flex", flexWrap: "wrap", gap: 8 }}>
          {modules.map((item) => (
            <PermissionToggle
              key={item.id}
              item={item}
              checked={permissionAllowed.includes(item.id)}
              onChange={onToggle}
              disabled={disabled}
              title={disabledReason}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── AddRole page ── */
const AddRole = ({ isOnboarding = false, onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleid, viewRoleId } = useParams();
  const isEdit = Boolean(roleid);
  const isView = Boolean(viewRoleId);
  const { setLoading } = usePageLoader();
  const { selectedOrganization: globalOrgId } = useSelectedOrganization();
  const [viewMode, setViewMode] = useState(false);
  const [btnLoader, setBtnLoader] = useState(false);
  const [permissionAllowed, setPermissionAllowed] = useState([]);
  const [viewRoleData, setViewRoleData] = useState();
  const [organizationList, setOrganizationList] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    if (isEdit || isView) return null;
    const onboardingOrg =
      localStorage.getItem("onboarding_org_id") || localStorage.getItem("organizationId");
    return onboardingOrg ? Number(onboardingOrg) : null;
  });
  const [moduleData, setModuleData] = useState(
    Object.fromEntries(Object.keys(MenuConstants).map((k) => [k, []])),
  );

  const pageTitle = isEdit ? editRoleLabel : isView ? viewRoleLabel : addRoleLabel;

  const isDefaultRole =
    !!location.state?.isDefault ||
    !!viewRoleData?.is_default ||
    ["superadmin", "admin", "hr", "staff"].includes(viewRoleData?.role_name?.trim()?.toLowerCase());

  /* ── Ref for scroll-to-error on submit ── */
  const roleNameRef = useRef(null);

  /* ── Track initial permissions for edit-mode dirty check ── */
  const initialPermissions = useRef([]);
  const [initialPermissionsState, setInitialPermissionsState] = useState([]);
  const [initialRoleName, setInitialRoleName] = useState("");

  const visibleModuleIds = useMemo(() => {
    return Object.values(moduleData)
      .flat()
      .map((m) => m.id);
  }, [moduleData]);

  /* ── Total permissions count ── */
  const totalModules = visibleModuleIds.length;
  const grantedCount = useMemo(() => {
    return permissionAllowed.filter((id) => visibleModuleIds.includes(id)).length;
  }, [permissionAllowed, visibleModuleIds]);
  const pct = totalModules > 0 ? Math.round((grantedCount / totalModules) * 100) : 0;
  const pctColor = pct > 60 ? "#10b981" : pct > 30 ? "#f59e0b" : "#64748b";

  const currentUserRole = localStorage.getItem("role");
  const currentUserPermissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("permissions")) || [];
    } catch {
      return [];
    }
  }, []);

  const hasSalesPermission = useMemo(() => {
    if (currentUserRole === SuperAdmin) return true;
    return currentUserPermissions.some(
      (p) =>
        p.parent_module_name?.trim()?.toLowerCase() === "sales" ||
        p.module_name?.trim()?.toLowerCase() === "sales",
    );
  }, [currentUserRole, currentUserPermissions]);

  const hasSupportPermission = useMemo(() => {
    if (currentUserRole === SuperAdmin) return true;
    return currentUserPermissions.some(
      (p) =>
        p.parent_module_name?.trim()?.toLowerCase() === "support" ||
        p.module_name?.trim()?.toLowerCase() === "support",
    );
  }, [currentUserRole, currentUserPermissions]);

  const fetchMasterModules = useCallback(
    (orgId) => {
      const resolvedOrgId = orgId || Number(localStorage.getItem("organizationId"));
      if (!resolvedOrgId) return;
      setLoading(true);
      listOfRolePermissionApi({ organization_id: Number(resolvedOrgId) })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const data = resp?.data?.listOfRolePermission;
            const updated = {};
            Object.keys(MenuConstants).forEach((key) => {
              if (key === "SALES" && !hasSalesPermission) {
                updated[key] = [];
                return;
              }
              if (key === "SUPPORT" && !hasSupportPermission) {
                updated[key] = [];
                return;
              }
              updated[key] =
                data?.filter(
                  (item) =>
                    item?.parent_module_name === MenuConstants[key] &&
                    !(
                      item?.parent_module_name === "Master" &&
                      item?.module_name?.trim()?.toLowerCase() === "organization"
                    ),
                ) || [];
            });
            setModuleData(updated);
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.data?.response?.message || err?.message))
        .finally(() => setLoading(false));
    },
    [setLoading, hasSalesPermission, hasSupportPermission],
  );

  /* ── Fetch role data ── */
  const ViewRole = useCallback(
    (id) => {
      setLoading(true);
      viewRolePermissionApi(id)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            setViewRoleData(resp?.data);
            const isAdmin = resp?.data?.role_name?.trim()?.toLowerCase() === "admin";
            const perms = (resp?.data?.rolePermission || [])
              .filter((item) => {
                if (isAdmin) {
                  const parent = item?.masterModule?.parent_module_name || item?.parent_module_name;
                  if (parent === "APP") return false;
                }
                return true;
              })
              .map((item) => item?.masterModule.id);
            setPermissionAllowed(perms);
            initialPermissions.current = [...perms];
            setInitialPermissionsState([...perms]);
            setInitialRoleName(resp?.data?.role_name || "");
            const orgId = resp?.data?.organization?.id ?? resp?.data?.organizationId;
            if (orgId) {
              setSelectedOrganization(Number(orgId));
              fetchMasterModules(Number(orgId));
            }
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.data?.response?.message || err?.message))
        .finally(() => setLoading(false));
    },
    [setLoading, fetchMasterModules],
  );

  useEffect(() => {
    if (isView) {
      setViewMode(true);
      ViewRole(viewRoleId);
    }
    if (isEdit) {
      ViewRole(roleid);
    }
  }, [isView, isEdit, viewRoleId, roleid, ViewRole]);

  useEffect(() => {
    if (localStorage.getItem("role") === SuperAdmin) {
      listOfOrganizationApi()
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const list = resp?.data?.listOfOrganizations || [];
            setOrganizationList(
              list.map((item) => ({
                value: item.id,
                label: item.organization_name,
              })),
            );
          }
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("role") === SuperAdmin) return;
    if (isEdit || isView) return;

    const org = localStorage.getItem("onboarding_org_id") || localStorage.getItem("organizationId");
    if (org) {
      setSelectedOrganization(Number(org));
      fetchMasterModules(Number(org));
    }
  }, [isEdit, isView, fetchMasterModules]);

  useEffect(() => {
    if (localStorage.getItem("role") !== SuperAdmin) return;
    if (isEdit || isView) return;

    let resolvedOrgId = null;
    if (globalOrgId) {
      resolvedOrgId = Number(globalOrgId);
    } else {
      const onboardingOrg =
        localStorage.getItem("onboarding_org_id") || localStorage.getItem("organizationId");
      if (onboardingOrg) {
        resolvedOrgId = Number(onboardingOrg);
      }
    }

    if (resolvedOrgId) {
      setSelectedOrganization(resolvedOrgId);
      fetchMasterModules(resolvedOrgId);
    }
  }, [isEdit, isView, globalOrgId, fetchMasterModules]);

  /* ── Formik ── */
  const roleForm = useFormik({
    enableReinitialize: true,
    initialValues: { role: isEdit || isView ? viewRoleData?.role_name || "" : "" },
    validationSchema: yup.object({
      role: yup.string().required(validationMessages.required(roleNameLabel)),
    }),
    onSubmit: (values) => {
      if (permissionAllowed.length === 0) {
        toast.error("Please select at least one module permission before saving.");
        return;
      }
      setBtnLoader(true);
      const orgFromStorage =
        Number(localStorage.getItem("onboarding_org_id")) ||
        Number(localStorage.getItem("organizationId"));
      const payload = {
        role_name: values.role,
        master_module_id: permissionAllowed,
        organizationId: selectedOrganization ?? orgFromStorage ?? null,
      };
      const apiCall = isEdit ? editRoleApi(roleid, payload) : addRoleApi(payload);

      apiCall
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message || "Role created successfully!");
            setPermissionAllowed([]);
            if (isOnboarding && onSuccess) {
              onSuccess(resp?.data?.id ?? resp?.data);
            } else {
              navigate("/role");
            }
          } else toast.error(resp?.message);
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message))
        .finally(() => {
          setBtnLoader(false);
        });
    },
  });

  /* ── Scroll to error field on failed submit ── */
  useEffect(() => {
    if (roleForm.submitCount > 0 && roleForm.errors.role) {
      if (roleNameRef.current) {
        roleNameRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = roleNameRef.current.querySelector("input");
        if (input) input.focus();
      }
    }
  }, [roleForm.submitCount, roleForm.errors.role]);

  useEffect(() => {
    const isAdmin =
      roleForm.values.role?.trim()?.toLowerCase() === "admin" ||
      viewRoleData?.role_name?.trim()?.toLowerCase() === "admin";

    if (isAdmin && moduleData["APP"]) {
      const appModuleIds = moduleData["APP"].map((m) => m.id);
      setPermissionAllowed((prev) => prev.filter((id) => !appModuleIds.includes(id)));
    }
  }, [roleForm.values.role, viewRoleData, moduleData]);

  /* ── Dirty check for edit mode ── */
  const isUnchanged = useMemo(() => {
    if (!isEdit) return false;
    if (initialRoleName === "" && initialPermissionsState.length === 0) return false;
    const nameUnchanged = roleForm.values.role.trim() === initialRoleName.trim();
    const sortedCurrent = [...permissionAllowed].sort((a, b) => a - b);
    const sortedInitial = [...initialPermissionsState].sort((a, b) => a - b);
    const permsUnchanged =
      sortedCurrent.length === sortedInitial.length &&
      sortedCurrent.every((id, i) => id === sortedInitial[i]);
    return nameUnchanged && permsUnchanged;
  }, [isEdit, roleForm.values.role, initialRoleName, permissionAllowed, initialPermissionsState]);

  /* ── Permission handlers ── */
  const handleCheckboxChange = (id) => {
    let clickedItem = null;
    let clickedCategoryKey = null;
    Object.entries(moduleData).forEach(([catKey, modules]) => {
      const found = modules.find((m) => m.id === id);
      if (found) {
        clickedItem = found;
        clickedCategoryKey = catKey;
      }
    });

    let webModuleId = null;
    if (moduleData["WEB"]) {
      const webMod = moduleData["WEB"].find((m) => m.module_name?.trim()?.toUpperCase() === "WEB");
      if (webMod) webModuleId = webMod.id;
    }

    const isWebClick = clickedItem?.module_name?.trim()?.toUpperCase() === "WEB";

    setPermissionAllowed((prev) => {
      const isChecking = !prev.includes(id);
      if (isChecking) {
        if (isWebClick) {
          const autoCheckIds = [];
          Object.entries(moduleData).forEach(([categoryKey, modules]) => {
            if (categoryKey !== "SALES" && categoryKey !== "APP") {
              modules.forEach((m) => {
                autoCheckIds.push(m.id);
              });
            }
          });
          return [...new Set([...prev, id, ...autoCheckIds])];
        } else {
          if (clickedCategoryKey !== "APP" && clickedCategoryKey !== "SALES" && webModuleId) {
            return [...new Set([...prev, id, webModuleId])];
          }
          return [...prev, id];
        }
      } else {
        if (isWebClick) {
          const keepIds = [];
          Object.entries(moduleData).forEach(([categoryKey, modules]) => {
            if (categoryKey === "SALES" || categoryKey === "APP") {
              modules.forEach((m) => {
                if (prev.includes(m.id)) {
                  keepIds.push(m.id);
                }
              });
            }
          });
          return keepIds;
        } else {
          return prev.filter((p) => p !== id);
        }
      }
    });
  };

  const handleSelectAll = (key, isChecked) => {
    const ids = moduleData[key].map((m) => m.id);
    let webModuleId = null;
    if (moduleData["WEB"]) {
      const webMod = moduleData["WEB"].find((m) => m.module_name?.trim()?.toUpperCase() === "WEB");
      if (webMod) webModuleId = webMod.id;
    }

    setPermissionAllowed((prev) => {
      let nextState;
      if (isChecked) {
        nextState = [...new Set([...prev, ...ids])];
        if (key === "WEB") {
          const autoCheckIds = [];
          Object.entries(moduleData).forEach(([categoryKey, modules]) => {
            if (categoryKey !== "SALES" && categoryKey !== "APP") {
              modules.forEach((m) => {
                autoCheckIds.push(m.id);
              });
            }
          });
          nextState = [...new Set([...nextState, ...autoCheckIds])];
        } else if (key !== "SALES" && key !== "APP" && webModuleId) {
          nextState.push(webModuleId);
          nextState = [...new Set(nextState)];
        }
      } else {
        if (key === "WEB") {
          const keepIds = [];
          Object.entries(moduleData).forEach(([categoryKey, modules]) => {
            if (categoryKey === "SALES" || categoryKey === "APP") {
              modules.forEach((m) => {
                if (prev.includes(m.id)) {
                  keepIds.push(m.id);
                }
              });
            }
          });
          nextState = keepIds;
        } else {
          nextState = prev.filter((p) => !ids.includes(p));
        }
      }
      return nextState;
    });
  };

  const handleSelectAllGlobal = (checked) => {
    if (checked) {
      const isAdminRole =
        roleForm.values.role?.trim()?.toLowerCase() === "admin" ||
        viewRoleData?.role_name?.trim()?.toLowerCase() === "admin";

      const allIds = [];
      Object.entries(moduleData).forEach(([key, modules]) => {
        if (!(isAdminRole && key === "APP")) {
          modules.forEach((m) => allIds.push(m.id));
        }
      });
      setPermissionAllowed(allIds);
    } else {
      setPermissionAllowed([]);
    }
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      {!isOnboarding && (
        <div
          className="animate-slide-down"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 12,
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: "1.375rem",
                  fontWeight: 800,
                  margin: 0,
                  color: "var(--text-primary)",
                }}
              >
                {pageTitle}
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  marginTop: 2,
                }}
              >
                {isView ? "Viewing role permissions" : "Configure role name and module access"}
              </p>
            </div>
            {rendorBackButton(() => navigate("/role"))}
          </div>
        </div>
      )}

      <form
        onSubmit={roleForm.handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        {/* ── Role info card ── */}
        <div
          className="animate-slide-up"
          style={{
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
            overflow: "hidden",
          }}
        >
          <div
            style={{ height: 3, background: "linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)" }}
          />
          <div style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  background: "rgba(99,102,241,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Shield size={15} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                Role Details
              </h2>
            </div>

            <div
              ref={roleNameRef}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: 16,
              }}
            >
              <BaseInput
                name="role"
                label={roleNameLabel}
                placeholder={InputPlaceHolder(roleNameLabel)}
                value={roleForm.values.role}
                touched={roleForm.touched.role}
                disabled={viewMode || btnLoader || isDefaultRole}
                error={roleForm.errors.role}
                handleChange={roleForm.handleChange}
                handleBlur={roleForm.handleBlur}
                required
              />
              {localStorage.getItem("role") === SuperAdmin && (
                <BaseSelect
                  id="organization"
                  name="organization"
                  label="Organization"
                  options={organizationList}
                  placeholder="Select Organization"
                  value={selectedOrganization}
                  handleChange={(field, selected) => {
                    const orgVal = selected ? Number(selected) : null;
                    setSelectedOrganization(orgVal);
                    if (orgVal) fetchMasterModules(orgVal);
                  }}
                  required
                  handleBlur={() => {}}
                  isDisabled={!!globalOrgId || viewMode || btnLoader}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Permissions card ── */}
        <div
          className="animate-slide-up"
          style={{
            animationDelay: "60ms",
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
            overflow: "hidden",
          }}
        >
          <div style={{ height: 3, background: "linear-gradient(90deg,#10b981,#6366f1)" }} />

          {/* Permissions header */}
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid var(--glass-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                {masterModulesLabel}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: "0.75rem", color: "var(--text-muted)" }}>
                Toggle which modules this role can access
              </p>
            </div>

            {/* Progress + global toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {/* Progress pill */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  borderRadius: 10,
                  background: "var(--icon-btn-bg)",
                  border: "1px solid var(--glass-border)",
                }}
              >
                <div
                  style={{
                    width: 80,
                    height: 6,
                    borderRadius: 999,
                    background: "var(--glass-border)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      borderRadius: 999,
                      width: `${pct}%`,
                      background: "linear-gradient(90deg,#6366f1,#10b981)",
                      transition: "width 0.4s ease",
                    }}
                  />
                </div>
                <span style={{ fontSize: "0.8125rem", fontWeight: 700, color: pctColor }}>
                  {pct}%
                </span>
                <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                  {grantedCount}/{totalModules}
                </span>
              </div>

              {/* All / None */}
              {!viewMode && (
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    disabled={btnLoader}
                    onClick={() => handleSelectAllGlobal(true)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 8,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      border: "1px solid rgba(16,185,129,0.3)",
                      background: "rgba(16,185,129,0.08)",
                      color: "#10b981",
                      cursor: btnLoader ? "not-allowed" : "pointer",
                      opacity: btnLoader ? 0.6 : 1,
                    }}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    disabled={btnLoader}
                    onClick={() => handleSelectAllGlobal(false)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 8,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      border: "1px solid rgba(244,63,94,0.3)",
                      background: "rgba(244,63,94,0.08)",
                      color: "#f43f5e",
                      cursor: btnLoader ? "not-allowed" : "pointer",
                      opacity: btnLoader ? 0.6 : 1,
                    }}
                  >
                    None
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Category sections */}
          <div style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
            {Object.keys(MenuConstants).map((key) => {
              const modules = moduleData[key];
              if (!modules || modules.length === 0) return null;

              const isAdminRole =
                roleForm.values.role?.trim()?.toLowerCase() === "admin" ||
                viewRoleData?.role_name?.trim()?.toLowerCase() === "admin";

              const isAppKey = key === "APP";
              const isStaffAppDisable = isAdminRole && isAppKey;
              const disabledReason = isStaffAppDisable
                ? "admin cannot access staff app"
                : undefined;

              return (
                <CategorySection
                  key={key}
                  categoryKey={key}
                  modules={modules}
                  permissionAllowed={permissionAllowed}
                  onToggle={handleCheckboxChange}
                  onSelectAll={handleSelectAll}
                  disabled={viewMode || btnLoader || isStaffAppDisable}
                  disabledReason={disabledReason}
                />
              );
            })}
          </div>
        </div>

        {/* ── Submit ── */}
        {!viewMode && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingBottom: 8 }}>
            {!isOnboarding && (
              <BaseButton
                color="cancel"
                type="button"
                disabled={btnLoader}
                onClick={() => navigate("/role")}
              >
                Cancel
              </BaseButton>
            )}
            <BaseButton
              color="primary"
              type="submit"
              loader={btnLoader}
              disabled={btnLoader || permissionAllowed.length < 1 || (isEdit && isUnchanged)}
            >
              {isEdit ? Update : Submit}
            </BaseButton>
          </div>
        )}
      </form>
    </div>
  );
};

export default AddRole;
